import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save/RequestFormSaveContext";
import { useEditShipmentLock } from "@RequestFormV2/context/editShipmentLock";
import { ProviderOption, SelectedParcelAccount } from "@RequestFormV2/sections/Provider/types";
import { buildAllProviderSelections } from "@RequestFormV2/sections/Provider/utils/buildAllProviderSelections";
import { shouldAutoSelectAllProviders } from "@RequestFormV2/sections/Provider/utils/defaultProviderInvitation";
import { getProviderSelectionKey } from "@RequestFormV2/sections/Provider/utils/getProviderSelectionKey";
import {
  isAllProvidersSelected,
  mergeLateAppearingProvidersIfAllPreviousSelected,
} from "@RequestFormV2/sections/Provider/utils/providerSelection";
import { DefaultProvidersInvitations } from "types/Permissions";

type UseAutoSelectProvidersByDefaultParams = {
  providerOptions: ProviderOption[];
  minimumInvitesPossible: number;
  defaultProvidersInvitation?: string;
};

export function useAutoSelectProvidersByDefault({
  providerOptions,
  minimumInvitesPossible,
  defaultProvidersInvitation,
}: UseAutoSelectProvidersByDefaultParams) {
  const { getValues, setValue } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const saveContextRef = useRef(saveContext);
  saveContextRef.current = saveContext;
  const isEditShipmentLock = useEditShipmentLock();
  const previousProviderKeysRef = useRef<string[]>([]);

  useEffect(() => {
    if (isEditShipmentLock || providerOptions.length === 0) {
      return;
    }

    const currentProviderKeys = providerOptions.map(getProviderSelectionKey);
    const selectedProviders: string[] = getValues("invited_providers.providers") ?? [];
    const selectedParcelAccounts: SelectedParcelAccount[] = getValues("invited_providers.parcel_accounts") ?? [];

    const persistSelection = (providers: string[], parcel_accounts: SelectedParcelAccount[]) => {
      setValue("invited_providers.providers", providers);
      setValue("invited_providers.parcel_accounts", parcel_accounts);
      saveContextRef.current?.saveField("invited_providers", { providers, parcel_accounts }).catch(() => undefined);
    };

    if (
      shouldAutoSelectAllProviders(
        defaultProvidersInvitation,
        selectedProviders.length,
        minimumInvitesPossible,
        providerOptions.length,
      )
    ) {
      if (!isAllProvidersSelected(providerOptions, selectedProviders, selectedParcelAccounts)) {
        const { providers, parcel_accounts } = buildAllProviderSelections(providerOptions);
        persistSelection(providers, parcel_accounts);
      }
      previousProviderKeysRef.current = currentProviderKeys;
      return;
    }

    if (defaultProvidersInvitation === DefaultProvidersInvitations.PROVIDERS_INVITATION_ALL_OPTIONAL) {
      const merged = mergeLateAppearingProvidersIfAllPreviousSelected(
        previousProviderKeysRef.current,
        providerOptions,
        selectedProviders,
        selectedParcelAccounts,
      );

      if (merged) {
        persistSelection(merged.providers, merged.parcel_accounts);
      }
    }

    previousProviderKeysRef.current = currentProviderKeys;
  }, [
    defaultProvidersInvitation,
    getValues,
    isEditShipmentLock,
    minimumInvitesPossible,
    providerOptions,
    setValue,
  ]);
}
